/**
 * Meta Muse connector — client-safe constants.
 *
 * Meta Muse (Meta's personal AI agent, launched 2026-09-08) has no
 * "add MCP server" setting. A user connects a service by TELLING Muse about
 * it: Muse reads the service's API docs or MCP URL, builds a bridge on its
 * own VM with the official MCP SDK (streamable HTTP), asks for credentials
 * through its Secure Credentials Store (the agent only ever holds a surrogate
 * token — Sentinel swaps in the real key at the network boundary) and saves
 * the result as a reusable skill. Directory listing goes through
 * https://muse.ai/platform (partner-reviewed, see docs/muse-connector-submission.md).
 *
 * This module holds the pieces both the /mcp landing page (client) and the
 * connector brief route (server) need, so keep it free of server imports.
 */

// Literal on purpose: `@/constants/mcpServer` imports this module for its
// Muse client entry, so importing MCP_ENDPOINT back would be a cycle.
export const MUSE_MCP_ENDPOINT = 'https://easy-peasy.ai/mcp';

/** Markdown connector brief Muse reads while building the bridge. */
export const MUSE_CONNECTOR_BRIEF_PATH = '/connectors/muse.md';
export const MUSE_CONNECTOR_BRIEF_URL = `https://easy-peasy.ai${MUSE_CONNECTOR_BRIEF_PATH}`;

/** MCP server card (registry server.json shape) for agent discovery. */
export const MCP_SERVER_CARD_PATH = '/.well-known/mcp/server-card.json';

export const MUSE_PLATFORM_URL = 'https://muse.ai/platform';

/**
 * The paste-ready message a user sends to Muse. Follows the pattern Meta's
 * help center describes for custom connectors (name the service, hand over
 * the entry point, say what you want to be able to do) and the convention
 * early Muse connectors converged on (build → test → save as a skill).
 *
 * The API key is deliberately NOT part of the message: Muse must collect it
 * through its credential prompt so it lands in the Secure Credentials Store
 * instead of the chat transcript.
 */
export const MUSE_CONNECT_PROMPT = `Build a custom connector for Easy-Peasy.AI.

Its MCP server is ${MUSE_MCP_ENDPOINT} (Streamable HTTP, JSON responses, POST only). Authenticate every request with my Easy-Peasy.AI API key sent as the "x-api-key" header — ask me for the key through your secure credentials prompt; I will paste it from https://easy-peasy.ai/settings. Read the connector brief at ${MUSE_CONNECTOR_BRIEF_URL} first.

I want you to be able to write with Easy-Peasy.AI's 200+ templates, generate and edit images, create videos, turn text into speech with any of its voices, and transcribe audio — all on my account.

Connect, call get_account to confirm the key works, list the available tools, run one quick test (list_templates), then save this connector as a reusable skill.`;
